import type { ProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import { PS_MAX_CASCADER_SELECTION } from '@/features/project/_utils/constants';
import {
  AmountSearchBudgetTypeEnumModifier,
  formulaTrendConditionLabelModifier,
} from '@/features/search/_models/projects-search';
import type {
  AmountSearchConditionSchema,
  ProjectSearchFormType,
} from '@/features/search/_models/projects-search';
import { t } from '@/i18n';
import {
  type AmountUnit,
  ITEM_SPLITTER,
  formatValueByUnit,
  isPS,
} from '@/utils';
import type {
  FieldValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import type { z } from 'zod';
import type { SharedSearchFormType } from '../_models/shared-search';

export const PROJECT_SEARCH_TAG_TEST_ID = 'projects-search-field-tags';

export type ProjectsSearchAccordionProviderType =
  | 'project'
  | 'group'
  | 'analysis';

export type InputCascaderAndAccountingLabels = {
  nestedOrganizationIds: string[];
  nestedOtherResponsibleOrganizationIds: string[];
  nestedPolicyIds: string[];
  nestedExpenditureBudgetPurposeIds: string[];
  accountingTypeIds: string[];
};

export type ProjectsSearchSubmitType = {
  values: ProjectSearchFormType;
  newCascadeId: string | undefined | null;
  inputCascaderAndAccountingLabels: InputCascaderAndAccountingLabels;
};

export const COL_MIN_WIDTH = {
  basic: 200,
  large: 400,
} as const;

export const getSearchKeyword = (
  q: string | null,
  isSearchTargetProjectName: boolean | null,
): string | null => {
  if (!q) {
    return null;
  }

  return `${q}${isSearchTargetProjectName ? `（${t('project-name')}）` : ''}`;
};

export const handleCascaderOnChange = (
  key:
    | 'nestedOrganizationIds'
    | 'nestedPolicyIds'
    | 'nestedOtherResponsibleOrganizationIds'
    | 'nestedExpenditureBudgetPurposeIds',
  value: string[][],
  setValue: UseFormSetValue<SharedSearchFormType>,
  openMessage: (type: 'warning', message: string) => void,
) => {
  const selectionLength = value?.length ?? 0;
  if (isPS() && selectionLength > PS_MAX_CASCADER_SELECTION) {
    openMessage(
      'warning',
      t('warning-message-cascader-selection', {
        limit: PS_MAX_CASCADER_SELECTION,
      }),
    );
  } else {
    setValue(key, value);
  }
};

export const resetAnalyticTrendFields = ({
  setValue,
  index,
}: {
  setValue: UseFormSetValue<ProjectSearchFormType>;
  index: number;
}) => {
  setValue(`formulaConditions.${index}.baseAmountYear`, null);
  setValue(`formulaConditions.${index}.baseAmountType`, null);
  setValue(`formulaConditions.${index}.baseAmountValueIsnull`, false);
  setValue(`formulaConditions.${index}.baseAmountValueFrom`, null);
  setValue(`formulaConditions.${index}.baseAmountValueTo`, null);
  setValue(`formulaConditions.${index}.targetAmountYear`, null);
  setValue(`formulaConditions.${index}.targetAmountType`, null);
  setValue(`formulaConditions.${index}.trendConditionValue`, null);
  setValue(`formulaConditions.${index}.trendCondition`, null);
};

export const validateAmountSearchCondition = ({
  formulaCondition,
}: {
  formulaCondition: z.infer<typeof AmountSearchConditionSchema>[number];
}): {
  hasNullField: boolean;
  hasMinMaxError?: boolean;
} => {
  const {
    conditionType,
    baseAmountYear,
    baseAmountType,
    baseAmountValueFrom,
    baseAmountValueIsnull,
    baseAmountValueTo,
    targetAmountYear,
    targetAmountType,
    trendConditionValue,
    trendCondition,
  } = formulaCondition;

  const noSetValue = [
    baseAmountYear,
    baseAmountType,
    baseAmountValueFrom,
    baseAmountValueIsnull ? true : null,
    baseAmountValueTo,
    targetAmountYear,
    targetAmountType,
    trendConditionValue,
    trendCondition,
  ].every((v) => v === null);

  if (noSetValue) {
    return { hasNullField: false };
  }

  const hasValidThresholdBaseAmountValue =
    baseAmountValueIsnull === true ||
    typeof baseAmountValueFrom === 'number' ||
    typeof baseAmountValueTo === 'number';

  const trendValue = trendCondition === 'noChange' ? true : trendConditionValue;

  const targets =
    conditionType === 'threshold'
      ? [baseAmountYear, baseAmountType, hasValidThresholdBaseAmountValue]
      : [
          baseAmountYear,
          baseAmountType,
          targetAmountYear,
          targetAmountType,
          trendValue,
          trendCondition,
        ];

  const hasMinMaxError =
    conditionType === 'threshold' &&
    typeof baseAmountValueFrom === 'number' &&
    typeof baseAmountValueTo === 'number' &&
    baseAmountValueFrom > baseAmountValueTo;

  return {
    hasNullField:
      !targets.every((v) => v === null) &&
      targets.some((v) => v === null || v === false),
    hasMinMaxError,
  };
};

export const getAmountSearchTagLabel = ({
  formulaCondition,
  fromHistory,
}: {
  formulaCondition: z.infer<typeof AmountSearchConditionSchema>[number];
  fromHistory?: boolean;
}) => {
  const { hasNullField } = validateAmountSearchCondition({
    formulaCondition,
  });

  if (hasNullField) {
    return null;
  }

  const {
    conditionType,
    baseAmountYear,
    baseAmountType,
    baseAmountValueFrom,
    baseAmountValueTo,
    baseAmountValueIsnull,
    targetAmountYear,
    targetAmountType,
    trendConditionValue,
    trendCondition,
  } = formulaCondition;

  // 基準年度と予算種別のラベル
  const baseBudgetTypeLabel = baseAmountType
    ? AmountSearchBudgetTypeEnumModifier[baseAmountType]
    : '';

  const yearBudgetTypeLabel = `${baseAmountYear} ${baseBudgetTypeLabel}`;
  if (conditionType === 'threshold') {
    if (baseAmountValueIsnull) {
      // 未入力の場合
      return [yearBudgetTypeLabel, t('threshold-none')].join(ITEM_SPLITTER);
    }
    const isNumberBaseAmountValueFrom = typeof baseAmountValueFrom === 'number';
    const isNumberBaseAmountValueTo = typeof baseAmountValueTo === 'number';
    const unitText =
      baseAmountType === 'executionRate'
        ? t('percentage-unit')
        : t('thousand-yen-unit');

    const unit: AmountUnit =
      baseAmountType === 'executionRate' || !fromHistory ? 'plain' : 'thousand';

    if (isNumberBaseAmountValueFrom && isNumberBaseAmountValueTo) {
      // 両方指定
      return [
        yearBudgetTypeLabel,
        `${t('amount-with-unit', { amount: formatValueByUnit(baseAmountValueFrom, { unit }), unit: unitText })}${'~'}${t('amount-with-unit', { amount: formatValueByUnit(baseAmountValueTo, { unit }), unit: unitText })}`,
      ].join(ITEM_SPLITTER);
    }
    if (isNumberBaseAmountValueFrom) {
      // 最大のみ
      return [
        yearBudgetTypeLabel,
        t('amount-with-unit', {
          amount: formatValueByUnit(baseAmountValueFrom, { unit }),
          unit: unitText,
        }),
        t('threshold-greater'),
      ].join(ITEM_SPLITTER);
    }
    if (isNumberBaseAmountValueTo) {
      // 最小のみ
      return [
        yearBudgetTypeLabel,
        t('amount-with-unit', {
          amount: formatValueByUnit(baseAmountValueTo, { unit }),
          unit: unitText,
        }),
        t('threshold-lesser'),
      ].join(ITEM_SPLITTER);
    }
  } else if (conditionType === 'trend') {
    const targetBudgetTypeLabel = targetAmountType
      ? AmountSearchBudgetTypeEnumModifier[targetAmountType]
      : '';
    const yearTargetBudgetTypeLabel = `${targetAmountYear} ${targetBudgetTypeLabel}`;

    if (trendCondition === 'noChange') {
      // 変化なしの場合
      return [
        `${yearBudgetTypeLabel} → ${yearTargetBudgetTypeLabel}`,
        t('trend-no-change'),
      ].join(ITEM_SPLITTER);
    }

    // 増減条件のラベル生成
    const trendLabel = trendCondition
      ? formulaTrendConditionLabelModifier[trendCondition]
      : '';

    // trendConditionValueがnullの場合は早期リターン
    if (trendConditionValue === null || trendConditionValue === undefined) {
      return null;
    }
    const unit: AmountUnit =
      trendCondition === 'decreaseGtePercent' ||
      trendCondition === 'increaseGtePercent' ||
      !fromHistory
        ? 'plain'
        : 'thousand';
    return [
      `${yearBudgetTypeLabel} → ${yearTargetBudgetTypeLabel}`,
      `${formatValueByUnit(trendConditionValue, { unit })}${trendLabel}`,
    ].join(ITEM_SPLITTER);
  }

  return null;
};

export const validateProjectNumber = <
  // projectNumbers を持つフォームスキーマで再利用可能にするための制約。
  T extends FieldValues & { projectNumbers: string | null },
>(
  values: Pick<T, 'projectNumbers'>,
  setError: UseFormSetError<T>,
) => {
  const { projectNumbers } = values;
  const isHalfWidthNumeric = /^[0-9]+$/.test(projectNumbers ?? '');
  if (projectNumbers && !isHalfWidthNumeric) {
    // projectNumbers は T のキーであることを上の制約で保証している。
    setError('projectNumbers' as Parameters<UseFormSetError<T>>[0], {
      message: t('validation.half-width-numeric-required'),
    });
    return { hasError: true };
  }
  return { hasError: false };
};

// 任意項目だが初期値の設定が必要な値を取得
export const getInitialValueOfOptionalFields = ({
  searchQueryStates,
  initialFiscalYearByGroup,
}: {
  searchQueryStates: ProjectsPageSearchQueryStates;
  initialFiscalYearByGroup: string | undefined;
}) => {
  // 意図的に未設定にしているのか、queryが設定されていないのか判定するためにlocation.searchを使用

  return {
    fiscalYear: initialFiscalYearByGroup ?? searchQueryStates.fiscalYear,
    skipAdministrativeReformTeamEvaluation: isPS()
      ? null
      : searchQueryStates.skipAdministrativeReformTeamEvaluation === 'all'
        ? null
        : searchQueryStates.skipAdministrativeReformTeamEvaluation,
  };
};
